import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../env';
import {AppLogger, AppLoggerService} from '../../core/logging';
import {RatingListItem} from './recipe.service';

/**
 * Client for the recipe rating endpoints.
 *
 * Access rule is inherited from recipes: if you can open the recipe you can
 * rate it and read its ratings, so there is no separate permission gating here.
 *
 * `rate` / `clear` both return 204 (no body) — the caller is responsible for
 * reconciling the aggregate summary optimistically (see RecipeRatingsComponent).
 */
@Injectable({
  providedIn: 'root'
})
export class RecipeRatingService {
  private readonly log: AppLogger;

  constructor(
    private http: HttpClient,
    appLogger: AppLoggerService
  ) {
    this.log = appLogger.createForClass(RecipeRatingService);
  }

  /**
   * Set (or replace) my rating.
   * PUT /api/recipes/:id/rating  → 204
   */
  rate(recipeId: number, score: number, comment = ''): Observable<void> {
    this.log.debug('Rate recipe', {recipeId, score, hasComment: comment.length > 0});

    return this.http.put<void>(
      `${environment.apiUrl}/recipes/${recipeId}/rating`,
      {score, comment}
    );
  }

  /**
   * Clear my rating.
   * DELETE /api/recipes/:id/rating  → 204
   */
  clear(recipeId: number): Observable<void> {
    this.log.debug('Clear recipe rating', {recipeId});

    return this.http.delete<void>(
      `${environment.apiUrl}/recipes/${recipeId}/rating`
    );
  }

  /**
   * The comment view: everyone's ratings and notes. Loaded on demand.
   * GET /api/recipes/:id/ratings
   */
  list(recipeId: number): Observable<RatingListItem[]> {
    this.log.debug('List recipe ratings', {recipeId});

    return this.http.get<RatingListItem[]>(
      `${environment.apiUrl}/recipes/${recipeId}/ratings`
    );
  }
}
